import { create } from 'zustand';

// Matches backend NotificationResponse struct (serde camelCase)
export interface Notification {
  notificationId: number;
  userId: number;
  notificationType: string;
  category: string;
  priority: string;
  title: string;
  message: string;
  sourceType: string | null;
  sourceId: number | null;
  actionUrl: string | null;
  isRead: boolean;
  readAt: string | null;
  isDismissed: boolean;
  expiresAt: string | null;
  createdDate: string;
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  addNotification: (notification: Notification) => void;
  markAsRead: (notificationId: number) => void;
  markAllAsRead: () => void;
  setNotifications: (notifications: Notification[]) => void;
  setConnected: (connected: boolean) => void;
  removeNotification: (notificationId: number) => void;
}

export const useNotificationStore = create<NotificationState>()((set) => ({
  notifications: [],
  unreadCount: 0,
  isConnected: false,
  addNotification: (notification) => {
    set(state => ({
      notifications: [notification, ...state.notifications].slice(0, 100),
      unreadCount: state.unreadCount + (notification.isRead ? 0 : 1),
    }));
  },
  markAsRead: (notificationId) => {
    set(state => ({
      notifications: state.notifications.map(n =>
        n.notificationId === notificationId ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
      ),
      unreadCount: Math.max(0, state.unreadCount - 1),
    }));
  },
  markAllAsRead: () => {
    set(state => ({
      notifications: state.notifications.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() })),
      unreadCount: 0,
    }));
  },
  setNotifications: (notifications) => {
    set({
      notifications,
      unreadCount: notifications.filter(n => !n.isRead).length,
    });
  },
  setConnected: (connected) => set({ isConnected: connected }),
  removeNotification: (notificationId) => {
    set(state => {
      const removed = state.notifications.find(n => n.notificationId === notificationId);
      return {
        notifications: state.notifications.filter(n => n.notificationId !== notificationId),
        unreadCount: removed && !removed.isRead ? state.unreadCount - 1 : state.unreadCount,
      };
    });
  },
}));
